import { APITypesV1 } from "@cur8/api-client";
import {
  BottomSheet,
  Button,
  Icon16Close,
  Icon24Checkmark,
  IconButton,
  Typography,
} from "@cur8/maneki";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Checkbox } from "react-aria-components";
import { useConfirmDrawMutation } from "hooks/mutations/useConfirmDrawMutation";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import {
  orderedTubes,
  resolutionFor,
  sampleTypeLabels,
  TubeResolution,
} from "lib/tubeStatus";
import { CheckInParams } from "lib/types/checkInParameters";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { Banner } from "render/ui/presentation/Banner";
import { BarcodeScanner } from "render/ui/presentation/BarcodeScanner";
import { CheckCard } from "render/ui/presentation/CheckCard";
import { Group } from "render/ui/presentation/Group";
import { RadioGroup } from "render/ui/presentation/RadioGroup";
import { Link } from "render/ui/trigger/Link";
import styles from "./styles.module.sass";

type DrawConfirmationViewProps = {
  goTo(url: string): void;
  onBack(): void;
} & CheckInParams;

const SKIP_REASONS: APITypesV1.SkipReason[] = [
  APITypesV1.SkipReason.PatientDeclined,
  APITypesV1.SkipReason.PatientUnwell,
  APITypesV1.SkipReason.TechnicalIssue,
  APITypesV1.SkipReason.Other,
];

const SKIP_REASON_LABELS: Record<APITypesV1.SkipReason, string> = {
  [APITypesV1.SkipReason.PatientDeclined]: "Member declined",
  [APITypesV1.SkipReason.PatientUnwell]: "Member unwell",
  [APITypesV1.SkipReason.TechnicalIssue]: "Technical issue",
  [APITypesV1.SkipReason.Other]: "Other",
};

type PendingDecision =
  | { decision: "drawn" }
  | { decision: "skipped"; reason: APITypesV1.SkipReason };

export function DrawConfirmationView({
  goTo,
  onBack,
  patientId,
  visitId,
}: DrawConfirmationViewProps) {
  const { data: patient } = usePatientQuery({ patientId });
  const bloodSamplesQuery = useVisitBloodSamplesQuery({ patientId, visitId });
  const samplesLoaded = bloodSamplesQuery.data != null;
  const tubes = useMemo(
    () => orderedTubes(bloodSamplesQuery.data?.bloodSamples),
    [bloodSamplesQuery.data]
  );

  const queryClient = useQueryClient();
  const confirmDrawMutation = useConfirmDrawMutation();

  // Freely toggleable, local-only until Done is pressed — the backend has no
  // way to reverse an already-confirmed tube, so nothing is sent per tap.
  const [pendingDecisions, setPendingDecisions] = useState<
    Record<string, PendingDecision>
  >({});
  const [skipTarget, setSkipTarget] = useState<APITypesV1.BloodSample | null>(
    null
  );
  const [skipReason, setSkipReason] = useState<APITypesV1.SkipReason | "">("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Tubes matched by a scan this session. Manual mode reveals everything
  // regardless, and already-resolved tubes stay revealed too, so a
  // partially-done visit doesn't get hidden behind a re-scan.
  const [scannedIds, setScannedIds] = useState<Record<string, true>>({});
  const [manualMode, setManualMode] = useState(false);
  const [scanToken, setScanToken] = useState(0);
  const rearm = useCallback(() => setScanToken((n) => n + 1), []);

  // Already submitted in a prior session — the backend can't reverse this,
  // so it's shown read-only regardless of local pending state.
  const isLocked = useCallback(
    (sample: APITypesV1.BloodSample) => resolutionFor(sample) !== "pending",
    []
  );

  const isRevealed = useCallback(
    (sample: APITypesV1.BloodSample) =>
      manualMode ||
      scannedIds[sample.bloodSampleId] === true ||
      isLocked(sample) ||
      pendingDecisions[sample.bloodSampleId] != null,
    [manualMode, scannedIds, isLocked, pendingDecisions]
  );

  const allResolved =
    tubes.length > 0 &&
    tubes.every(
      (tube) => isLocked(tube) || pendingDecisions[tube.bloodSampleId] != null
    );
  const allRevealed = tubes.length > 0 && tubes.every(isRevealed);

  const handleScan = useCallback(
    (scannedCode: string) => {
      const match = tubes.find((tube) => tube.bloodSampleId === scannedCode);
      if (!match) {
        setErrorMessage(
          `Scanned barcode ${scannedCode} doesn't match any tube for this visit.`
        );
        rearm();
        return;
      }
      setErrorMessage(undefined);
      setScannedIds((prev) => ({ ...prev, [match.bloodSampleId]: true }));
      rearm();
    },
    [tubes, rearm]
  );

  const handleScanError = useCallback(
    (error: Error) => {
      setErrorMessage(`Camera error: ${error.message}`);
      rearm();
    },
    [rearm]
  );

  const enableManualMode = useCallback(() => {
    setErrorMessage(undefined);
    setManualMode(true);
  }, []);

  const enableScanMode = useCallback(() => {
    setErrorMessage(undefined);
    setManualMode(false);
  }, []);

  const toggleConfirm = useCallback(
    (bloodSampleId: string, checked: boolean) => {
      setPendingDecisions((prev) => {
        if (!checked) {
          const next = { ...prev };
          delete next[bloodSampleId];
          return next;
        }
        return { ...prev, [bloodSampleId]: { decision: "drawn" } };
      });
    },
    []
  );

  const undoPending = useCallback((bloodSampleId: string) => {
    setPendingDecisions((prev) => {
      const next = { ...prev };
      delete next[bloodSampleId];
      return next;
    });
  }, []);

  const openSkip = useCallback((sample: APITypesV1.BloodSample) => {
    setSkipReason("");
    setSkipTarget(sample);
  }, []);

  const closeSkip = useCallback(() => {
    setSkipTarget(null);
    setSkipReason("");
  }, []);

  const confirmSkip = useCallback(() => {
    if (!skipTarget || !skipReason) {
      return;
    }
    setPendingDecisions((prev) => ({
      ...prev,
      [skipTarget.bloodSampleId]: { decision: "skipped", reason: skipReason },
    }));
    closeSkip();
  }, [closeSkip, skipReason, skipTarget]);

  const handleDone = useCallback(() => {
    const drawnBloodSampleIds = tubes
      .filter(
        (tube) => pendingDecisions[tube.bloodSampleId]?.decision === "drawn"
      )
      .map((tube) => tube.bloodSampleId);
    const skippedBloodSamples = tubes.flatMap((tube) => {
      const decision = pendingDecisions[tube.bloodSampleId];
      return decision?.decision === "skipped"
        ? [{ bloodSampleId: tube.bloodSampleId, reason: decision.reason }]
        : [];
    });

    if (drawnBloodSampleIds.length === 0 && skippedBloodSamples.length === 0) {
      goTo(paths.checkInSuccess.url({ patientId, visitId }));
      return;
    }

    setErrorMessage(undefined);
    confirmDrawMutation.mutate(
      { patientId, drawnBloodSampleIds, skippedBloodSamples },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["visitBloodSamples", patientId, visitId],
          });
          goTo(paths.checkInSuccess.url({ patientId, visitId }));
        },
        onError: (error: unknown) => {
          setErrorMessage(
            `Failed to record draw confirmation: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        },
      }
    );
  }, [
    confirmDrawMutation,
    goTo,
    patientId,
    pendingDecisions,
    queryClient,
    tubes,
    visitId,
  ]);

  const fullName =
    [patient?.name?.firstName, patient?.name?.lastName]
      .filter(Boolean)
      .join(" ") ||
    patient?.name?.displayName ||
    "the member";
  const dateOfBirth = patient?.dateOfBirth?.toFormat("d MMMM yyyy");

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">Confirm tubes</Typography>
            <Typography variant="body-m" color="subtle">
              Confirm the member&apos;s identity against the label before the
              tube leaves the room.
            </Typography>
            {!manualMode && !allRevealed && samplesLoaded ? (
              <div className={styles.scanSection}>
                <BarcodeScanner
                  onScan={handleScan}
                  onError={handleScanError}
                  active
                  resetToken={scanToken}
                />
                <Link
                  onPress={enableManualMode}
                  className={styles.secondaryLink}
                >
                  Unable to scan? Confirm manually
                </Link>
              </div>
            ) : undefined}
            <div className={styles.memberRow}>
              <div className={styles.detailRow}>
                <Typography variant="label-s" color="subtle">
                  Member
                </Typography>
                <Typography variant="body-m">{fullName}</Typography>
              </div>
              <div className={styles.detailRow}>
                <Typography variant="label-s" color="subtle">
                  Date of birth
                </Typography>
                <Typography variant="body-m">{dateOfBirth}</Typography>
              </div>
            </div>
            {errorMessage ? (
              <Banner kind="error" compact>
                {errorMessage}
              </Banner>
            ) : undefined}
            {tubes.map((tube, index) => {
              if (!isRevealed(tube)) {
                return (
                  <div
                    key={tube.bloodSampleId}
                    className={styles.pendingScanRow}
                  >
                    <Typography variant="body-s" color="subtle">
                      {index + 1}. Scan the {sampleTypeLabels[tube.sampleType]}{" "}
                      tube
                    </Typography>
                  </div>
                );
              }

              const locked = isLocked(tube);

              return (
                <TubeCard
                  key={tube.bloodSampleId}
                  tube={tube}
                  index={index}
                  locked={locked}
                  lockedResolution={locked ? resolutionFor(tube) : undefined}
                  pending={pendingDecisions[tube.bloodSampleId]}
                  isSubmitting={confirmDrawMutation.isPending}
                  onToggleConfirm={(checked) =>
                    toggleConfirm(tube.bloodSampleId, checked)
                  }
                  onSkip={() => openSkip(tube)}
                  onUndoSkip={() => undoPending(tube.bloodSampleId)}
                />
              );
            })}
          </section>
        }
        buttons={
          <div className={styles.doneSection}>
            {manualMode && !allResolved ? (
              <Link
                onPress={enableScanMode}
                className={`${styles.secondaryLink} ${styles.scanModeLink}`}
              >
                Use the scanner instead
              </Link>
            ) : undefined}
            <ActionsFooter
              left={
                <Button
                  variant="secondary"
                  state={confirmDrawMutation.isPending ? "disabled" : undefined}
                  onPress={onBack}
                  fillContainer
                >
                  Back
                </Button>
              }
              right={
                <Button
                  state={
                    !allResolved || confirmDrawMutation.isPending
                      ? "disabled"
                      : undefined
                  }
                  onPress={handleDone}
                  fillContainer
                >
                  Done
                </Button>
              }
            />
          </div>
        }
      />

      <BottomSheet
        isOpen={skipTarget != null}
        onClose={closeSkip}
        showCloseIcon={false}
      >
        <div className={styles.skipSheet}>
          <div className={styles.skipSheetContent}>
            <div className={styles.skipSheetHeader}>
              <Typography variant="display-s">Add skip reason</Typography>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={closeSkip}
              >
                <Icon16Close />
              </IconButton>
            </div>
            <div className={styles.skipSheetDescription}>
              <Typography variant="body-m" color="subtle">
                This will be logged. No draw event will be recorded for this
                tube.
              </Typography>
            </div>
            <RadioGroup
              aria-label="Reason the tube wasn't drawn"
              value={skipReason}
              onChange={(value) =>
                setSkipReason(value as APITypesV1.SkipReason)
              }
            >
              {SKIP_REASONS.map((reason) => (
                <RadioGroup.Radio
                  key={reason}
                  value={reason}
                  label={SKIP_REASON_LABELS[reason]}
                />
              ))}
            </RadioGroup>
          </div>
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={closeSkip} fillContainer>
                Close
              </Button>
            }
            right={
              <Button
                state={!skipReason ? "disabled" : undefined}
                onPress={confirmSkip}
                fillContainer
              >
                Skip tube
              </Button>
            }
          />
        </div>
      </BottomSheet>
    </PageFrameContent>
  );
}

function TubeCard({
  tube,
  index,
  locked,
  lockedResolution,
  pending,
  isSubmitting,
  onToggleConfirm,
  onSkip,
  onUndoSkip,
}: {
  tube: APITypesV1.BloodSample;
  index: number;
  locked: boolean;
  lockedResolution: TubeResolution | undefined;
  pending: PendingDecision | undefined;
  isSubmitting: boolean;
  onToggleConfirm(checked: boolean): void;
  onSkip(): void;
  onUndoSkip(): void;
}) {
  const typeLabel = sampleTypeLabels[tube.sampleType];
  const confirmLabel = `${index + 1}. Confirm ${typeLabel} tube`;
  const isDrawnSelected = pending?.decision === "drawn";

  return (
    <Group
      highlight={
        (locked && lockedResolution === "drawn") || isDrawnSelected
          ? "blue"
          : "none"
      }
    >
      <div className={styles.tubeCard}>
        <div className={styles.tubeMeta}>
          <Typography variant="label-m">{typeLabel}</Typography>
          <div className={styles.sampleIdRow}>
            <Typography variant="label-s" color="subtle">
              Sample ID
            </Typography>
            <Typography variant="body-s">{tube.bloodSampleId}</Typography>
          </div>
        </div>

        {locked ? (
          lockedResolution === "skipped" ? (
            <Typography variant="body-s" color="subtle">
              Skipped
            </Typography>
          ) : (
            <div className={styles.drawnCheckCard}>
              <CheckCard
                aria-label={confirmLabel}
                isSelected
                onChange={() => undefined}
              >
                <Typography variant="body-m">
                  I confirm label matches member name and date of birth
                </Typography>
              </CheckCard>
            </div>
          )
        ) : pending?.decision === "skipped" ? (
          <div className={styles.confirmActionsRow}>
            <Typography variant="body-s" color="subtle">
              Skipped: {SKIP_REASON_LABELS[pending.reason]}
            </Typography>
            <Link
              onPress={onUndoSkip}
              isDisabled={isSubmitting}
              className={styles.secondaryLink}
            >
              Undo
            </Link>
          </div>
        ) : (
          <div className={styles.confirmRows}>
            <div className={styles.confirmActionsRow}>
              <Typography variant="body-m">
                I confirm label matches member name and date of birth
              </Typography>
              <ConfirmToggle
                ariaLabel={confirmLabel}
                isSelected={isDrawnSelected}
                isDisabled={isSubmitting}
                onToggle={onToggleConfirm}
              />
            </div>
            <Link
              onPress={onSkip}
              isDisabled={isSubmitting}
              className={styles.secondaryLink}
            >
              Skip drawing this tube
            </Link>
          </div>
        )}
      </div>
    </Group>
  );
}

function ConfirmToggle({
  ariaLabel,
  isSelected,
  isDisabled,
  onToggle,
}: {
  ariaLabel: string;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle(checked: boolean): void;
}) {
  return (
    <Checkbox
      aria-label={ariaLabel}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onChange={onToggle}
      className={[
        styles.confirmToggle,
        isSelected ? styles.confirmToggleChecked : "",
      ].join(" ")}
    >
      <Icon24Checkmark />
    </Checkbox>
  );
}
